import { ReactElement } from 'react';

import styled from 'styled-components';

import { Card } from '@components/card';
import { IconProps } from '@components/icons';
import { Stack, StackProps } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';

export type StateItemProps = {
  icon?: ReactElement<IconProps>;
  selected?: boolean;
  label: string;
  description?: string;
  onSelected?: VoidFunction;
  isDisabled?: boolean;
  alignment?: 'left' | 'center';
} & Pick<StackProps, 'orientation'>;

export const StateSelector = ({
  icon,
  selected = false,
  label,
  description,
  orientation,
  onSelected,
  isDisabled = false,
  alignment = 'left',
}: StateItemProps) => {
  const justify = orientation === 'vertical' ? 'center' : alignment;

  return (
    <StateCard
      className="state-card"
      space={4}
      onClick={onSelected}
      $selected={selected}
      $isDisabled={isDisabled}
      data-selected={selected}
    >
      <Stack align="center" justify={justify} orientation={orientation} space={2}>
        <IconWrapper $selected={selected}>{icon}</IconWrapper>
        <Stack align={orientation !== 'vertical' ? 'left' : 'center'} justify={justify}>
          <Text
            color={selected ? 'elementBlue100' : 'textPrimary'}
            font="semibold12"
            text={label}
          />
          <Text
            color={selected ? 'elementBlue100' : 'textSecondary'}
            font="regular12"
            text={description}
          />
        </Stack>
      </Stack>
    </StateCard>
  );
};

const StateCard = styled(Card)<{ $selected: boolean; $isDisabled: boolean }>((p) => {
  return {
    ...(p.$selected
      ? {
          '&[data-variant="default"]': {
            border: '2px solid',
            borderColor: theme.color.elementBlue100,
            padding: 'calc(var(--card-space) - 1px)',
          },
        }
      : !p.$isDisabled
      ? {
          ':hover, :focus': {
            cursor: 'pointer',
            backgroundColor: theme.color.uiTrans5,
          },
        }
      : {}),
    width: '100%',
    ...(p.$isDisabled && { cursor: 'not-allowed', opacity: 0.5 }),
  };
});

const IconWrapper = styled.div.attrs({ className: 'state-card-icon-wrapper' })<{
  $selected: boolean;
}>((p) => {
  return {
    color: p.$selected ? theme.color.elementBlue100 : theme.color.purple50,
  };
});
